/**
 * Allys Contact Form Section Component
 * 
 * Modern, sophisticated contact form with Allys theming
 */

'use client';

import { memo } from 'react';
import { ContactFormSection, type ContactFormField } from '@/components/contact/ContactFormSection';
import { Envelope, Phone, MapPin, CurrencyDollar } from '@phosphor-icons/react';

// Helper to safely get string values from messages
const safeGetString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  return fallback;
};

// Helper to safely get object values
const safeGetObject = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>;
  return {};
};

export interface AllysContactFormSectionProps {
  messages?: Record<string, unknown>;
  locale?: string;
  onSubmit?: (data: Record<string, string>) => Promise<void>;
}

export const AllysContactFormSection = memo(function AllysContactFormSection({
  messages,
  locale: _locale,
  onSubmit
}: AllysContactFormSectionProps) {
  const contactMessages = messages?.contact as Record<string, unknown> | undefined;
  const formMessages = safeGetObject(contactMessages?.form);
  const propertyTypeOptions = safeGetObject(formMessages?.propertyTypeOptions);

  const fields: ContactFormField[] = [
    {
      name: 'firstName',
      label: safeGetString(formMessages?.firstName, 'First Name'),
      placeholder: safeGetString(formMessages?.firstNamePlaceholder, 'John'),
      type: 'text',
      required: true,
    },
    {
      name: 'lastName',
      label: safeGetString(formMessages?.lastName, 'Last Name'),
      placeholder: safeGetString(formMessages?.lastNamePlaceholder, 'Doe'),
      type: 'text',
      required: true,
    },
    {
      name: 'email',
      label: safeGetString(formMessages?.email, 'Email Address'),
      placeholder: safeGetString(formMessages?.emailPlaceholder, 'john@example.com'),
      type: 'email',
      required: true,
    },
    {
      name: 'phone',
      label: safeGetString(formMessages?.phone, 'Phone Number'),
      placeholder: safeGetString(formMessages?.phonePlaceholder, '+230 XXXX XXXX'),
      type: 'tel',
      required: false,
    },
    {
      name: 'propertyType',
      label: safeGetString(formMessages?.propertyType, 'Property Type'),
      placeholder: safeGetString(formMessages?.propertyTypePlaceholder, 'Select property type'),
      type: 'select',
      required: false,
      options: [
        { value: 'residential', label: safeGetString(propertyTypeOptions?.residential, 'Residential') },
        { value: 'commercial', label: safeGetString(propertyTypeOptions?.commercial, 'Commercial') },
        { value: 'land', label: safeGetString(propertyTypeOptions?.land, 'Land') },
        { value: 'luxury', label: safeGetString(propertyTypeOptions?.luxury, 'Luxury Villa') },
        { value: 'apartment', label: safeGetString(propertyTypeOptions?.apartment, 'Apartment') },
        { value: 'other', label: safeGetString(propertyTypeOptions?.other, 'Other') },
      ],
    },
    {
      name: 'budget',
      label: safeGetString(formMessages?.budget, 'Budget Range'),
      placeholder: safeGetString(formMessages?.budgetPlaceholder, 'e.g., $500,000 - $1,000,000'),
      type: 'text',
      required: false,
    },
    {
      name: 'message',
      label: safeGetString(formMessages?.message, 'Your Message'),
      placeholder: safeGetString(formMessages?.messagePlaceholder, 'Tell us about your property requirements...'),
      type: 'textarea',
      required: true,
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <ContactFormSection
          title={safeGetString(formMessages?.title, 'Send Us an Enquiry')}
          subtitle={safeGetString(formMessages?.subtitle, "Fill out the form below and we'll get back to you within 24 hours")}
          fields={fields}
          submitLabel={safeGetString(formMessages?.submit, 'Send Enquiry')}
          sendingLabel={safeGetString(formMessages?.sending, 'Sending...')}
          successMessage={safeGetString(formMessages?.success, "Thank you! We'll be in touch shortly.")}
          errorMessage={safeGetString(formMessages?.error, 'Error sending message. Please try again.')}
          {...(onSubmit ? { onSubmit } : {})}
          className="allys-contact-form-section"
          renderField={(field, value, onChange, error) => {
            // Get icon based on field
            const getFieldIcon = () => {
              switch (field.name) {
                case 'email':
                  return <Envelope size={20} weight="light" className="text-gray-400" />;
                case 'phone':
                  return <Phone size={20} weight="light" className="text-gray-400" />;
                case 'budget':
                  return <CurrencyDollar size={20} weight="light" className="text-gray-400" />;
                case 'propertyType':
                  return <MapPin size={20} weight="light" className="text-gray-400" />;
                default:
                  return null;
              }
            };

            return (
              <div className="mb-6">
                <label
                  htmlFor={field.name}
                  className="block text-sm font-semibold text-gray-900 mb-2 font-inter"
                >
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                <div className="relative">
                  {field.type === 'textarea' ? (
                    <textarea
                      id={field.name}
                      name={field.name}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      placeholder={field.placeholder}
                      required={field.required}
                      rows={5}
                      className={`
                        w-full px-4 py-3 
                        border ${error ? 'border-red-500' : 'border-gray-300'} 
                        rounded-xl
                        focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent
                        transition-all duration-200
                        text-gray-900 placeholder:text-gray-400
                        resize-none
                        font-inter
                      `}
                    />
                  ) : field.type === 'select' ? (
                    <div className="relative">
                      {getFieldIcon() && (
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          {getFieldIcon()}
                        </div>
                      )}
                      <select
                        id={field.name}
                        name={field.name}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        required={field.required}
                        className={`
                          w-full ${getFieldIcon() ? 'pl-11' : 'pl-4'} pr-4 py-3
                          border ${error ? 'border-red-500' : 'border-gray-300'}
                          rounded-xl
                          focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent
                          transition-all duration-200
                          text-gray-900 placeholder:text-gray-400
                          appearance-none bg-white
                          cursor-pointer
                          font-inter
                        `}
                      >
                        <option value="">{field.placeholder}</option>
                        {field.options?.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      {getFieldIcon() && (
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          {getFieldIcon()}
                        </div>
                      )}
                      <input
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                        className={`
                          w-full ${getFieldIcon() ? 'pl-11' : 'pl-4'} pr-4 py-3
                          border ${error ? 'border-red-500' : 'border-gray-300'}
                          rounded-xl
                          focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent
                          transition-all duration-200
                          text-gray-900 placeholder:text-gray-400
                          font-inter
                        `}
                      />
                    </div>
                  )}
                </div>

                {error && (
                  <p className="mt-2 text-sm text-red-600 font-inter">
                    {error}
                  </p>
                )}
              </div>
            );
          }}
        />
        
        <style jsx global>{`
          .allys-contact-form-section .contact-form-submit {
            width: 100%;
            padding: 1rem 2rem;
            background: linear-gradient(135deg, var(--color-primary), var(--color-accent));
            color: white;
            font-weight: 600;
            font-size: 1rem;
            border-radius: 0.75rem;
            border: none;
            cursor: pointer;
            transition: all 0.3s ease;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }

          .allys-contact-form-section .contact-form-submit:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }

          .allys-contact-form-section .contact-form-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .allys-contact-form-section .contact-form-success {
            margin-top: 1rem;
            padding: 1rem;
            background-color: #10B981;
            color: white;
            border-radius: 0.75rem;
            text-align: center;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          .allys-contact-form-section .contact-form-error-message {
            margin-top: 1rem;
            padding: 1rem;
            background-color: #EF4444;
            color: white;
            border-radius: 0.75rem;
            text-align: center;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          .allys-contact-form-section .contact-form-header {
            text-align: center;
            margin-bottom: 3rem;
          }

          .allys-contact-form-section .contact-form-title {
            font-size: 2.25rem;
            font-weight: 700;
            color: #111827;
            margin-bottom: 1rem;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          .allys-contact-form-section .contact-form-subtitle {
            font-size: 1.125rem;
            color: #6B7280;
            max-width: 42rem;
            margin: 0 auto;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          @media (max-width: 640px) {
            .allys-contact-form-section .contact-form-title {
              font-size: 1.875rem;
            }
          }
        `}</style>
      </div>
    </section>
  );
});

